import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, FileSpreadsheet, CheckCircle2,
  AlertCircle, XCircle, ChevronRight, Download, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const STEP_LABELS = ["Seleccionar archivo", "Vista previa", "Confirmar importación"];

export default function OpsRRHHImportar() {
  const navigate = useNavigate();

  const [step, setStep]           = useState(0);
  const [file, setFile]           = useState(null);
  const [preview, setPreview]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [result, setResult]       = useState(null);
  const [dragging, setDragging]   = useState(false);
  const fileRef                   = useRef(null);

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;
    if (!selectedFile.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Solo se aceptan archivos .xlsx");
      return;
    }
    setFile(selectedFile);
    setPreview(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFileSelect(f);
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", "true");
      const r = await api.post("/ops/employees/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      setStep(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al analizar el archivo");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!file) return;
    if (!window.confirm(`¿Confirmar importación de ${preview?.would_create} colaboradores?`)) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", "false");
      const r = await api.post("/ops/employees/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      setStep(2);
      toast.success(`${r.data.inserted} colaboradores importados`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al importar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"
          onClick={() => navigate("/ops/rrhh/empleados")}
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>
        <ChevronRight className="w-4 h-4 text-slate-300" />
        <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5 text-[#FFDF00]" />
          Importar colaboradores desde Excel
        </h1>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2">
        {STEP_LABELS.map((label, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
              i < step ? "bg-emerald-500 text-white" :
              i === step ? "bg-slate-900 text-white" :
              "bg-slate-200 text-slate-500"
            }`}>
              {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
            </div>
            <span className={`text-sm ${i === step ? "font-medium text-slate-900" : "text-slate-400"}`}>
              {label}
            </span>
            {i < STEP_LABELS.length - 1 && <div className="w-8 h-px bg-slate-200 mx-1" />}
          </div>
        ))}
      </div>

      {/* Step 0 — Seleccionar archivo */}
      {step === 0 && (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
            <p className="font-semibold mb-1">Formato esperado</p>
            <p>El archivo debe ser la hoja <strong>BD_ALTAS</strong> del Excel ERP-CHIZY.xlsm, o cualquier XLSX con columnas:
            Nombre, Apellidos, CURP, RFC, NSS, Puesto, Sucursal, Sueldo, Fecha Ingreso.</p>
            <p className="mt-1 text-amber-700">La detección de columnas es tolerante a variaciones de nombre.</p>
          </div>

          {/* Drop zone */}
          <div
            data-testid="drop-zone"
            className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors cursor-pointer ${
              dragging ? "border-slate-900 bg-slate-50" :
              file ? "border-emerald-400 bg-emerald-50" :
              "border-slate-300 hover:border-slate-400"
            }`}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={e => handleFileSelect(e.target.files?.[0])}
              data-testid="file-input"
            />
            {file ? (
              <div className="space-y-2">
                <FileSpreadsheet className="w-10 h-10 text-emerald-500 mx-auto" />
                <p className="font-semibold text-emerald-800">{file.name}</p>
                <p className="text-sm text-emerald-600">{(file.size / 1024).toFixed(0)} KB</p>
                <p className="text-xs text-slate-400">Clic para cambiar el archivo</p>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="w-10 h-10 text-slate-300 mx-auto" />
                <p className="text-slate-600 font-medium">Arrastrá el archivo acá</p>
                <p className="text-sm text-slate-400">o clic para seleccionar (.xlsx)</p>
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button
              data-testid="analyze-btn"
              onClick={handlePreview}
              disabled={!file || loading}
              className="bg-slate-900 hover:bg-slate-800 text-white gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
              {loading ? "Analizando..." : "Analizar archivo"}
            </Button>
          </div>
        </div>
      )}

      {/* Step 1 — Vista previa */}
      {step === 1 && preview && (
        <div className="space-y-4">
          {/* Resumen */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-emerald-800">{preview.would_create}</p>
              <p className="text-xs text-emerald-600 mt-1">A importar</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-amber-800">{preview.would_skip}</p>
              <p className="text-xs text-amber-600 mt-1">Ya existen (skip)</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-red-800">{preview.errors?.length || 0}</p>
              <p className="text-xs text-red-600 mt-1">Errores</p>
            </div>
          </div>

          {/* Columnas detectadas */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-slate-600 mb-2">Columnas detectadas:</p>
            <div className="flex flex-wrap gap-2">
              {(preview.detected_columns || []).map(c => (
                <span key={c} className="text-xs bg-white border border-slate-200 px-2 py-0.5 rounded-full text-slate-700">
                  {c}
                </span>
              ))}
            </div>
          </div>

          {/* Warnings */}
          {preview.warnings?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-h-40 overflow-y-auto">
              <p className="text-xs font-semibold text-amber-700 mb-2">Advertencias ({preview.warnings.length}):</p>
              {preview.warnings.map((w, i) => (
                <p key={i} className="text-xs text-amber-700">{w}</p>
              ))}
            </div>
          )}

          {/* Preview tabla */}
          <div className="border border-slate-200 rounded-lg overflow-hidden max-h-80 overflow-y-auto">
            <table className="w-full text-xs" data-testid="preview-table">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-slate-600">Nombre</th>
                  <th className="px-3 py-2 text-left text-slate-600">CURP</th>
                  <th className="px-3 py-2 text-left text-slate-600">Puesto</th>
                  <th className="px-3 py-2 text-left text-slate-600">Sucursal</th>
                  <th className="px-3 py-2 text-left text-slate-600">Sueldo</th>
                  <th className="px-3 py-2 text-left text-slate-600">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(preview.preview || []).map((row, i) => (
                  <tr key={i} className={row.status === "skip" ? "opacity-50 bg-slate-50" : ""}>
                    <td className="px-3 py-2 font-medium text-slate-800">{row.name}</td>
                    <td className="px-3 py-2 text-slate-500 font-mono">{row.curp?.slice(0, 8)}...</td>
                    <td className="px-3 py-2 text-slate-600">{row.position}</td>
                    <td className="px-3 py-2 text-slate-600">{row.branch_name || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">
                      {row.salary_amount ? `$${Number(row.salary_amount).toLocaleString("es-MX")}` : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {row.status === "skip" ? (
                        <span className="text-amber-600 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> skip
                        </span>
                      ) : row.warnings?.length > 0 ? (
                        <span className="text-amber-600 flex items-center gap-1" title={row.warnings.join("; ")}>
                          <AlertCircle className="w-3 h-3" /> aviso
                        </span>
                      ) : (
                        <span className="text-emerald-600 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> OK
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(0)}>
              Volver
            </Button>
            <Button
              data-testid="confirm-import-btn"
              onClick={handleConfirm}
              disabled={loading || preview.would_create === 0}
              className="bg-slate-900 hover:bg-slate-800 text-white gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {loading ? "Importando..." : `Importar ${preview.would_create} colaboradores`}
            </Button>
          </div>
        </div>
      )}

      {/* Step 2 — Resultado */}
      {step === 2 && result && (
        <div className="space-y-4">
          <div className="text-center py-8">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900">Importación completada</h2>
            <div className="mt-4 flex gap-6 justify-center">
              <div className="text-center">
                <p className="text-3xl font-bold text-emerald-700">{result.inserted}</p>
                <p className="text-sm text-slate-500">Importados</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-amber-700">{result.skipped}</p>
                <p className="text-sm text-slate-500">Omitidos (ya existían)</p>
              </div>
              {result.errors?.length > 0 && (
                <div className="text-center">
                  <p className="text-3xl font-bold text-red-700">{result.errors.length}</p>
                  <p className="text-sm text-slate-500">Con errores</p>
                </div>
              )}
            </div>
          </div>

          {result.errors?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-red-700 mb-2">Registros con error:</p>
              {result.errors.map((err, i) => (
                <p key={i} className="text-xs text-red-600">{err.name}: {err.error}</p>
              ))}
            </div>
          )}

          <div className="flex justify-center">
            <Button
              onClick={() => navigate("/ops/rrhh/empleados")}
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              Ver colaboradores
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
